if (!customElements.get('fuji-purchase-bar')) {
  customElements.define('fuji-purchase-bar', class FujiPurchaseBar extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.action = this.querySelector('[data-purchase-submit]');
      this.contact = this.querySelector('[data-purchase-contact]');
      this.review = this.querySelector('[data-purchase-review]');
      this.optionText = this.querySelector('[data-purchase-options]');
      this.priceText = this.querySelector('[data-purchase-price]');
      if (!this.productInfo || !this.action || !this.review) return;
      this.reviewed = false;
      this.awaitingSubmit = false;
      this.cartFocusReturn = null;
      this.lifecycle = new AbortController();
      this.mobile = window.matchMedia('(max-width: 750px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.schedule = () => {
        if (this.frame || !this.isConnected) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.isConnected) this.sync();
        });
      };
      const options = { signal: this.lifecycle.signal };
      this.action.addEventListener('click', () => this.activate(), options);
      this.review.addEventListener('click', () => this.reviewSelection(), options);
      this.productInfo.addEventListener('change', event => {
        if (event.target.closest('variant-selects')) this.reviewed = true;
        this.schedule();
      }, options);
      this.productInfo.addEventListener('click', event => {
        if (event.target.closest('variant-selects input, variant-selects label, variant-selects select')) {
          this.reviewed = true;
          this.schedule();
        }
      }, options);
      this.productInfo.addEventListener('input', this.schedule, options);
      ['scroll', 'resize', 'pageshow'].forEach(name => window.addEventListener(name, this.schedule, { ...options, passive: true }));
      ['focusin', 'focusout', 'toggle', 'visibilitychange'].forEach(name => document.addEventListener(name, this.schedule, { ...options, capture: true }));
      this.mobile.addEventListener('change', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      window.visualViewport?.addEventListener('scroll', this.schedule, options);
      this.productObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.productObserver.observe(this.productInfo, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['class', 'hidden', 'disabled', 'aria-disabled', 'aria-busy', 'data-variant-state', 'data-release-blocked', 'value', 'min', 'max', 'step']
      });
      this.overlayObserver = new MutationObserver(records => {
        if (records.some(record => !this.productInfo.contains(record.target))) this.schedule();
      });
      this.overlayObserver.observe(document.body, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'open', 'hidden', 'aria-hidden']
      });
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this.productInfo);
      }
      this.sync();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.productObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.hidden = true;
      this.awaitingSubmit = false;
      this.cartFocusReturn = null;
    }

    nativeState() {
      const button = this.productInfo.querySelector('[id="ProductSubmitButton-' + this.dataset.sectionId + '"]');
      const form = button?.form;
      const variant = form?.querySelector('input[name="id"]');
      const productForm = button?.closest('product-form');
      const busy = button?.classList.contains('loading') || button?.getAttribute('aria-busy') === 'true' ||
        productForm?.getAttribute('aria-busy') === 'true' || this.productInfo.dataset.variantState === 'loading';
      const invalid = form && Array.from(form.elements).find(input => input.willValidate && !input.validity.valid);
      const error = productForm?.querySelector('.product-form__error-message-wrapper:not([hidden])');
      const disabled = !button || !form || !variant?.value || variant.disabled || button.disabled ||
        button.getAttribute('aria-disabled') === 'true' || busy || this.productInfo.dataset.variantState === 'error';
      return { button, form, variant, busy, invalid, error, disabled };
    }

    overlayOpen() {
      return Array.from(document.body.classList).some(name => name.startsWith('overflow-hidden') || name === 'fuji-menu-open') ||
        Boolean(document.querySelector('cart-drawer.active, cart-notification.active, cart-notification .cart-notification.active, dialog[open], modal-dialog[open], quick-add-modal[open], .header__search details[open], .fuji-mobile-menu[open], menu-drawer details[open], header-drawer details[open]'));
    }

    keyboardOpen(closedCart = null) {
      const active = document.activeElement;
      const editing = !closedCart?.contains(active) && active?.matches('textarea, select, [contenteditable="true"], input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]):not([type="button"]):not([type="submit"])');
      const viewport = window.visualViewport;
      return editing || Boolean(viewport && viewport.scale === 1 && window.innerHeight - viewport.height - viewport.offsetTop > 120);
    }

    selectedOptions() {
      const picker = this.productInfo.querySelector('variant-selects');
      return Array.from(picker?.querySelectorAll('input[type="radio"]:checked, select') || [])
        .map(input => input.value.trim()).filter(Boolean).join(' / ') || this.dataset.productTitle || '';
    }

    currentPrice(state) {
      if (!state.variant?.value || state.variant.disabled || state.busy || this.productInfo.dataset.variantState === 'error') return '';
      const container = this.productInfo.querySelector('[id="price-' + this.dataset.sectionId + '"]');
      if (!container || container.classList.contains('hidden') || container.hidden) return '';
      const price = container.querySelector('.price');
      const selector = price?.classList.contains('price--on-sale') ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular';
      const values = price?.querySelectorAll(selector);
      return values?.length ? values[values.length - 1].textContent.trim().replace(/\s+/g, ' ') : '';
    }

    sync() {
      if (!this.isConnected || !this.productInfo?.isConnected) return;
      const state = this.nativeState();
      const releaseContact = this.productInfo.querySelector('[data-release-blocked="true"] [data-fuji-release-contact]');
      const displayAction = releaseContact || state.button;
      this.action.hidden = Boolean(releaseContact);
      if (this.contact) {
        this.contact.hidden = !releaseContact;
        if (releaseContact) this.contact.href = releaseContact.href;
      }
      const text = state.button?.querySelector('span:not(.hidden):not(.loading__spinner)')?.textContent.trim() || state.button?.textContent.trim() || '';
      const reviewOnly = Boolean(state.invalid || state.error);
      const choose = this.dataset.hasOptions === 'true' && !this.reviewed;
      const label = !state.disabled && reviewOnly ? this.dataset.reviewLabel : !state.disabled && choose ? this.dataset.chooseLabel : text;
      if (this.action.textContent !== label) this.action.textContent = label;
      this.action.disabled = Boolean(state.disabled);
      this.action.setAttribute('aria-busy', String(Boolean(state.busy)));
      const selected = this.selectedOptions();
      const price = this.currentPrice(state);
      if (this.optionText.textContent !== selected) this.optionText.textContent = selected;
      if (this.priceText.textContent !== price) this.priceText.textContent = price;
      this.priceText.hidden = !price;
      const rect = displayAction?.getBoundingClientRect();
      const overlay = this.overlayOpen();
      if (overlay) {
        const cart = document.querySelector('cart-drawer.active') ||
          document.querySelector('cart-notification .cart-notification.active')?.closest('cart-notification');
        this.cartFocusReturn = cart && this.contains(cart.activeElement)
          ? { cart, target: cart.activeElement } : null;
      }
      const active = document.activeElement;
      if (!overlay && this.cartFocusReturn && active !== document.body && active !== document.documentElement &&
        !this.cartFocusReturn.cart.contains(active) && active !== this.cartFocusReturn.target) {
        this.cartFocusReturn = null;
      }
      const keyboard = this.keyboardOpen(!overlay ? this.cartFocusReturn?.cart : null);
      const visible = Boolean(this.mobile.matches && !document.hidden && !overlay && !keyboard &&
        rect && rect.width > 0 && rect.height > 0 && displayAction.getClientRects().length && rect.bottom <= 0);
      const focusedInside = this.contains(document.activeElement);
      this.hidden = !visible;
      if (!visible && focusedInside && !overlay && !keyboard && displayAction && !displayAction.disabled) {
        displayAction.focus({ preventScroll: true });
      }
      if (!overlay && !keyboard && this.cartFocusReturn) {
        const target = visible
          ? this.cartFocusReturn.target.disabled ? this.review : this.cartFocusReturn.target
          : displayAction;
        this.cartFocusReturn = null;
        if (target && !target.disabled && (visible || displayAction?.getClientRects().length)) target.focus({ preventScroll: true });
      }
      if (this.awaitingSubmit && !state.busy) {
        this.awaitingSubmit = false;
        if (state.error && !overlay) this.reviewSelection(true);
      }
    }

    reviewSelection(purchase = false) {
      if (!this.isConnected || !this.productInfo?.isConnected) return;
      const state = this.nativeState();
      const picker = this.productInfo.querySelector('variant-selects');
      const destination = purchase ? state.invalid || state.error || state.button : picker || state.error || state.button;
      if (!destination) return;
      this.reviewed = true;
      const focusTarget = destination.matches('input, select, button') ? destination :
        destination.querySelector('input:checked, select, button, input:not([type="hidden"])') || destination;
      if (!focusTarget.matches('input, select, button, a, [tabindex]')) focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
      destination.scrollIntoView({ block: 'center', behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
      this.schedule();
    }

    activate() {
      if (!this.isConnected || !this.productInfo?.isConnected) return;
      this.sync();
      if (this.hidden) return;
      const state = this.nativeState();
      if (state.disabled) return;
      if (state.invalid || state.error) {
        this.reviewSelection(true);
        return;
      }
      if (this.dataset.hasOptions === 'true' && !this.reviewed) {
        this.reviewSelection();
        return;
      }
      this.awaitingSubmit = true;
      state.button.click();
      this.schedule();
    }
  });
}
